import {
  getStringFromArrayBuffer,
  getUint8ArrayFromString,
  AlgorithmEnum,
  getSortedObjectString
} from "@zyzgroup/core-common";

const subtle = window.crypto.subtle;

export async function signature(
  text: string,
  algorithm = AlgorithmEnum.SHA256
): Promise<string> {
  if (!subtle) {
    throw new Error("Crypto API not supported");
  }
  const result = await subtle.digest(
    algorithm,
    getUint8ArrayFromString(text) as BufferSource
  );
  return getStringFromArrayBuffer(result);
}

export async function objectSignature(
  obj: any,
  algorithm = AlgorithmEnum.SHA256
): Promise<string> {
  return signature(JSON.stringify(getSortedObjectString(obj)), algorithm);
}

export async function encrypt(
  data: string,
  algorithm = AlgorithmEnum.AESGCM
): Promise<{
  algorithm: string;
  iv: Uint8Array;
  key: CryptoKey;
  encryptedData: ArrayBuffer;
}> {
  if (!subtle) {
    throw new Error("Crypto API not supported");
  }
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await subtle.generateKey(
    { name: algorithm, length: 256 },
    false, // defines key as extractable, use false b/c we do not need to store it
    ["encrypt", "decrypt"]
  );
  const encryptedData = await subtle.encrypt(
    { name: algorithm, iv },
    key,
    getUint8ArrayFromString(data) as BufferSource
  );
  return { algorithm, iv, key, encryptedData };
}

export async function decrypt(options: {
  algorithm: AlgorithmEnum;
  iv: Uint8Array;
  key: CryptoKey;
  encryptedData: ArrayBuffer;
}): Promise<string> {
  if (!subtle) {
    throw new Error("Crypto API not supported");
  }
  const decryptedData = await subtle.decrypt(
    {
      name: options.algorithm,
      iv: options.iv as BufferSource
    },
    options.key,
    options.encryptedData
  );
  return getStringFromArrayBuffer(decryptedData);
}

export async function signEncrypt(
  data: Record<string, any>,
  algorithm = AlgorithmEnum.ECDSA,
  hash = "SHA-384"
): Promise<{
  algorithm: string;
  hash: string;
  publicKey: CryptoKey;
  signature: ArrayBuffer;
  encodedData: Uint8Array;
}> {
  if (!subtle) {
    throw new Error("Crypto API not supported");
  }
  const { privateKey, publicKey } = await subtle.generateKey(
    {
      name: algorithm,
      namedCurve: "P-384"
    },
    false, // defines key as extractable, use false b/c we do not need to store it
    ["sign", "verify"]
  );
  const encodedData = getUint8ArrayFromString(
    JSON.stringify(data)
  ) as BufferSource;
  const signature = await subtle.sign(
    {
      name: algorithm,
      hash: { name: hash }
    },
    privateKey,
    encodedData
  );
  return {
    algorithm,
    hash,
    publicKey,
    signature,
    encodedData: encodedData as Uint8Array<ArrayBufferLike>
  };
}

export async function verifyDecrypt(options: {
  algorithm: AlgorithmEnum;
  hash: string;
  publicKey: CryptoKey;
  signature: ArrayBuffer;
  encodedData: Uint8Array;
}): Promise<boolean> {
  if (!subtle) {
    throw new Error("Crypto API not supported");
  }
  const isValid = await subtle.verify(
    {
      name: options.algorithm,
      hash: { name: options.hash }
    },
    options.publicKey,
    options.signature,
    options.encodedData as BufferSource
  );
  return isValid;
}
